import React, { useState, useEffect } from 'react';
import { DollarSign, Scissors, ShieldCheck, AlertCircle, Crown, LayoutGrid, User, ChevronRight, Download, ClipboardCheck, HelpCircle } from 'lucide-react';

// Visual treatment per contract status (mirrors the workbook's cell colors).
const STATUS_META = {
  standard: { label: 'Standard', badge: 'bg-gray-100 text-gray-700 border-gray-300', dot: 'bg-gray-400' },
  year2: { label: 'Contract · Yr 2', badge: 'bg-amber-100 text-amber-800 border-amber-300', dot: 'bg-amber-500' },
  final: { label: 'Contract · Final', badge: 'bg-rose-100 text-rose-800 border-rose-300', dot: 'bg-rose-500' },
  expired: { label: 'Expired (+$20)', badge: 'bg-emerald-100 text-emerald-800 border-emerald-300', dot: 'bg-emerald-500' },
};

// short acquisition label for the row subtitle (reflects how they were acquired,
// which for a traded player differs from the cost source)
const acqShort = (a) => {
  if (!a) return '';
  if (a.via === 'Trade') return 'traded';
  if (a.via === 'Offseason trade') return 'offseason trade';
  if (a.via === 'Kept') return 'kept';
  if (a.via === 'Auction draft') return 'drafted';
  if (a.via === '3-yr contract') return '3-yr deal';
  return 'added';
};

// Display order: expired → final (3rd yr) → year2 (2nd yr) → standard; desc cost within each.
const STATUS_ORDER = { expired: 0, final: 1, year2: 2, standard: 3, cut: 4 };
const byHierarchy = (a, b) =>
  (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || (b.keepCost - a.keepCost);

const meta = (status) => STATUS_META[status] || STATUS_META.standard;
const YR_LABEL = { year1: '1st yr', year2: '2nd yr', final: 'final yr' };

// Human-readable provenance for a player's detail panel.
const fmtAcquired = (a) => {
  if (!a) return null;
  if (a.via === 'Trade') return `Acquired via trade from ${a.from} on ${a.date}`;
  if (a.via === 'Offseason trade') return `Acquired via offseason trade from ${a.from} on ${a.date}`;
  if (a.via === 'Auction draft') return `Drafted · 2025 auction (pick #${a.pick}, $${a.cost})`;
  if (a.via === '3-yr contract') return `Signed to a 3-year contract in ${a.signedYear} at $${a.cost}`;
  if (a.via === 'Kept') return `Kept from 2025 roster · prior cost $${a.priorCost}`;
  return `Added via ${a.via}${a.date ? ` on ${a.date}` : ''} · ${a.faab != null ? `$${a.faab} FAAB bid` : 'free agent ($1)'}`;
};

const DetailRow = ({ label, children }) => (
  <div className="flex gap-3 text-sm">
    <span className="w-24 sm:w-28 flex-shrink-0 text-gray-400">{label}</span>
    <span className="text-gray-700">{children}</span>
  </div>
);

const PlayerDetail = ({ p }) => (
  <div className="bg-gray-50 border-t border-gray-100 px-4 sm:px-6 py-3 space-y-1.5">
    <DetailRow label="Acquired">{fmtAcquired(p.acquired)}</DetailRow>
    {p.contract && (
      <DetailRow label="Contract">
        Year {p.contract.year} of {p.contract.of} · signed {p.contract.signedYear} · flat ${p.contract.flatCost}
        {p.contract.cutCost != null && <> · would cost ${p.contract.cutCost} to cut</>}
      </DetailRow>
    )}
    <DetailRow label="2026 keep">
      {p.keepMath} = <span className="font-bold text-gray-900">${p.keepCost}</span>
    </DetailRow>
  </div>
);

// Compact card used in the "All teams" grid. Rows expand into the shared detail panel.
const TeamCard = ({ team, expandedKey, setExpandedKey }) => {
  const contracts = team.players.filter((p) => p.status !== 'standard').length;
  const total = team.players.reduce((a, p) => a + p.keepCost, 0);
  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-purple-800 to-indigo-900 px-3 py-2 text-white">
        <div className="font-bold text-sm leading-tight">{team.owner}</div>
        <div className="text-purple-200 text-[11px] flex justify-between gap-2">
          <span className="truncate">{team.teamName}</span>
          <span className="flex-shrink-0">{contracts} ctr · ${total}</span>
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {[...team.players].sort(byHierarchy).map((p, i) => {
          const key = `${team.teamId}-${i}`;
          const open = expandedKey === key;
          return (
            <div key={key}>
              <button
                onClick={() => setExpandedKey(open ? null : key)}
                className="w-full flex items-center gap-2 px-3 py-1 text-[13px] text-left hover:bg-gray-50"
              >
                <ChevronRight className={`h-3 w-3 flex-shrink-0 transition-transform ${open ? 'rotate-90 text-gray-500' : 'text-gray-300'}`} />
                <span className={`h-2 w-2 rounded-full flex-shrink-0 ${meta(p.status).dot}`} />
                <span className="flex-1 truncate text-gray-800" title={p.player}>{p.player}</span>
                {p.cutCost != null && (
                  <span className="inline-flex items-center text-[10px] text-rose-500" title="Cost to cut">
                    <Scissors className="h-2.5 w-2.5" />{p.cutCost}
                  </span>
                )}
                <span className="font-bold text-gray-900 w-8 text-right">${p.keepCost}</span>
              </button>
              {open && <PlayerDetail p={p} />}
            </div>
          );
        })}
      </div>
      {team.penalties.length > 0 && (
        <div className="bg-rose-50 border-t border-rose-100 px-3 py-1.5 mt-auto">
          {team.penalties.map((p, i) => (
            <div key={`pen-${i}`} className="flex items-center gap-2 text-[11px] text-rose-700" title={`Cut while under contract (${YR_LABEL[p.status] || p.status})`}>
              <Scissors className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="flex-1 truncate line-through opacity-75">{p.player}</span>
              <span className="font-bold">−${p.dock}</span>
            </div>
          ))}
        </div>
      )}
      {team.adjustments.length > 0 && (
        <div className={`bg-cyan-50 border-t border-cyan-100 px-3 py-1.5 ${team.penalties.length ? '' : 'mt-auto'}`}>
          {team.adjustments.map((a, i) => (
            <div key={`adj-${i}`} className="flex items-center gap-2 text-[11px]">
              <DollarSign className="h-2.5 w-2.5 flex-shrink-0 text-cyan-600" />
              <span className="flex-1 truncate text-cyan-800">{a.label}</span>
              <span className={`font-bold ${a.amount < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {a.amount < 0 ? '−' : '+'}${Math.abs(a.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// One team's declared keeper selections (Elective Keepers tab).
const ElectiveCard = ({ team }) => {
  if (!team.declared) {
    return (
      <div className="bg-white rounded-xl shadow border border-dashed border-gray-300 overflow-hidden flex flex-col opacity-70">
        <div className="bg-gray-100 px-3 py-2">
          <div className="font-bold text-sm leading-tight text-gray-600">{team.owner}</div>
          <div className="text-gray-400 text-[11px] truncate">{team.teamName}</div>
        </div>
        <div className="flex items-center justify-center gap-2 text-gray-400 text-sm py-8">
          <HelpCircle className="h-4 w-4" /> Awaiting selections
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden flex flex-col">
      <div className="bg-gradient-to-r from-emerald-800 to-teal-900 px-3 py-2 text-white">
        <div className="font-bold text-sm leading-tight">{team.owner}</div>
        <div className="text-emerald-200 text-[11px] flex justify-between gap-2">
          <span className="truncate">{team.teamName}</span>
          <span className="flex-shrink-0">{team.keeps.length} keeps · ${team.keepTotal}</span>
        </div>
      </div>
      <div className="divide-y divide-gray-50">
        {team.keeps.map((k, i) => (
          <div key={i} className="px-3 py-1 text-[13px]">
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${meta(k.status).dot}`} />
              <span className="flex-1 truncate text-gray-800" title={k.player}>{k.player}</span>
              {k.cutCost != null && (
                <span className="inline-flex items-center text-[10px] text-rose-500" title="Cost to cut">
                  <Scissors className="h-2.5 w-2.5" />{k.cutCost}
                </span>
              )}
              <span className="font-bold text-gray-900 w-8 text-right">${k.cost}</span>
            </div>
            {k.note && (
              <div className="pl-4 text-[11px] text-amber-700 font-medium">{k.note}</div>
            )}
          </div>
        ))}
      </div>
      {(team.cuts.length > 0 || team.penalties.length > 0) && (
        <div className="bg-rose-50 border-t border-rose-100 px-3 py-1.5">
          {team.cuts.map((c, i) => (
            <div key={`cut-${i}`} className="flex items-center gap-2 text-[11px] text-rose-700" title="Declining to keep — contract cut cost">
              <Scissors className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="flex-1 truncate line-through opacity-75">CUT: {c.player}</span>
              <span className="font-bold">−${c.dock}</span>
            </div>
          ))}
          {team.penalties.map((p, i) => (
            <div key={`pen-${i}`} className="flex items-center gap-2 text-[11px] text-rose-700" title={`Cut while under contract last season (${YR_LABEL[p.status] || p.status})`}>
              <Scissors className="h-2.5 w-2.5 flex-shrink-0" />
              <span className="flex-1 truncate line-through opacity-75">{p.player}</span>
              <span className="font-bold">−${p.dock}</span>
            </div>
          ))}
        </div>
      )}
      {team.adjustments.length > 0 && (
        <div className="bg-cyan-50 border-t border-cyan-100 px-3 py-1.5">
          {team.adjustments.map((a, i) => (
            <div key={`adj-${i}`} className="flex items-center gap-2 text-[11px]">
              <DollarSign className="h-2.5 w-2.5 flex-shrink-0 text-cyan-600" />
              <span className="flex-1 truncate text-cyan-800">{a.label}</span>
              <span className={`font-bold ${a.amount < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                {a.amount < 0 ? '−' : '+'}${Math.abs(a.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-auto bg-gray-900 text-white px-3 py-2 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">Draft budget</span>
        <span className="font-extrabold text-lg">${team.draftBudget}</span>
      </div>
    </div>
  );
};

// One team's newly signed 3-year contracts (the workbook's "3-Year Keeps" sheet).
const ThreeYearCard = ({ team, through }) => (
  <div className={`bg-white rounded-xl shadow border overflow-hidden flex flex-col ${team.declared ? 'border-gray-100' : 'border-dashed border-gray-300 opacity-70'}`}>
    <div className={`px-3 py-2 ${team.declared ? 'bg-gradient-to-r from-amber-600 to-orange-700 text-white' : 'bg-gray-100'}`}>
      <div className={`font-bold text-sm leading-tight ${team.declared ? '' : 'text-gray-600'}`}>{team.owner}</div>
      <div className={`text-[11px] flex justify-between gap-2 ${team.declared ? 'text-amber-100' : 'text-gray-400'}`}>
        <span className="truncate">{team.teamName}</span>
        {team.declared && (
          <span className="flex-shrink-0">{team.signings.length} signed · ${team.total}</span>
        )}
      </div>
    </div>
    {team.declared ? (
      <div className="divide-y divide-gray-50">
        {team.signings.map((s, i) => (
          <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
            <span className="h-2 w-2 rounded-full flex-shrink-0 bg-amber-500" />
            <span className="flex-1 truncate text-gray-800" title={s.player}>{s.player}</span>
            <span className="inline-flex items-center text-[10px] text-rose-500" title="Docked if cut during the signing season">
              <Scissors className="h-2.5 w-2.5" />{s.cutPenalty}
            </span>
            <span className="font-bold text-gray-900 w-8 text-right">${s.cost}</span>
          </div>
        ))}
      </div>
    ) : (
      <div className="flex items-center justify-center gap-2 text-gray-400 text-sm py-6">
        <HelpCircle className="h-4 w-4" /> No contract declared
      </div>
    )}
    {team.declared && (
      <div className="mt-auto bg-amber-50 border-t border-amber-100 px-3 py-1.5 text-[11px] text-amber-800">
        Flat through {through}
      </div>
    )}
  </div>
);

const Keepers = () => {
  const [data, setData] = useState(null);
  const [elective, setElective] = useState(null);
  const [threeYear, setThreeYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' | 'all' | 'elective'
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${process.env.PUBLIC_URL || ''}/keepers2026.json`);
        if (!res.ok) throw new Error(`Could not load keepers data (HTTP ${res.status})`);
        const json = await res.json();
        setData(json);
        setSelectedTeamId(json.teams[0]?.teamId ?? null);
        const eRes = await fetch(`${process.env.PUBLIC_URL || ''}/elective2026.json`);
        if (eRes.ok) setElective(await eRes.json());
        const tRes = await fetch(`${process.env.PUBLIC_URL || ''}/threeyear2026.json`);
        if (tRes.ok) setThreeYear(await tRes.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-purple-700">
        <div className="animate-spin h-8 w-8 border-4 border-purple-300 border-t-purple-700 rounded-full mr-3" />
        Loading keepers…
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-xl mx-auto mt-12 p-6 rounded-xl bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
        <AlertCircle className="h-6 w-6 flex-shrink-0" />
        <div>
          <p className="font-bold">Couldn’t load keeper data.</p>
          <p className="text-sm mt-1">{error}</p>
          <p className="text-sm mt-2 text-red-600">Run <code className="bg-red-100 px-1 rounded">node scripts/build/build2026.js</code> to generate it.</p>
        </div>
      </div>
    );
  }

  const team = data.teams.find((t) => t.teamId === selectedTeamId) || data.teams[0];
  const contractCount = team.players.filter((p) => p.status !== 'standard').length;
  const ifKeptAll = team.players.reduce((a, p) => a + p.keepCost, 0);

  const toggleBtn = (mode, Icon, label) => (
    <button
      onClick={() => setViewMode(mode)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
        viewMode === mode ? 'bg-purple-700 text-white shadow' : 'bg-white text-gray-600 border border-gray-200 hover:bg-purple-50'
      }`}
    >
      <Icon className="h-4 w-4" />{label}
    </button>
  );

  return (
    <div className={`${viewMode === 'all' ? 'max-w-7xl' : 'max-w-5xl'} mx-auto`}>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-900 flex items-center gap-2">
            <Crown className="h-8 w-8 text-yellow-500" /> {data.season} Keepers
          </h1>
          <p className="text-gray-500 mt-1">
            Keep cost, cut cost, and contract status for every rostered player — built from the {data.season - 1} season.
          </p>
        </div>
        <div className="flex gap-2">
          {toggleBtn('single', User, 'Single')}
          {toggleBtn('all', LayoutGrid, 'All teams')}
          {elective && toggleBtn('elective', ClipboardCheck, 'Elective keepers')}
          {threeYear && toggleBtn('threeyear', ShieldCheck, '3-year keepers')}
          <button
            onClick={async () => {
              const { downloadKeeperWorkbook } = await import('../keepers/exportWorkbook');
              downloadKeeperWorkbook(data, elective, threeYear);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-purple-50 transition-all"
          >
            <Download className="h-4 w-4" />Export .xlsx
          </button>
        </div>
      </div>

      {/* Status legend */}
      <div className="flex flex-wrap gap-3 mb-6">
        {Object.entries(STATUS_META).map(([key, m]) => (
          <div key={key} className="flex items-center gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
            <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
            <span className="font-semibold">{m.label}</span>
          </div>
        ))}
      </div>

      {viewMode === 'threeyear' && threeYear ? (
        /* ---------------- 3-year keepers grid ---------------- */
        <>
          <div className="mb-4 text-sm text-gray-500">
            3-year contracts signed after the {threeYear.season} draft, held flat at their auction
            price through {threeYear.through} ·{' '}
            {threeYear.teams.reduce((a, t) => a + t.signings.length, 0)} contracts ·{' '}
            {threeYear.teams.filter((t) => t.declared).length}/{threeYear.teams.length} teams declared
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {threeYear.teams.map((t) => (
              <ThreeYearCard key={t.teamId} team={t} through={threeYear.through} />
            ))}
          </div>
        </>
      ) : viewMode === 'elective' && elective ? (
        /* ---------------- Elective keepers grid ---------------- */
        <>
          <div className="mb-4 text-sm text-gray-500">
            Declared keeper selections for the {elective.season} draft ·{' '}
            {elective.teams.filter((t) => t.declared).length}/{elective.teams.length} teams in · updated {elective.updated}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {elective.teams.map((t) => (
              <ElectiveCard key={t.teamId} team={t} />
            ))}
          </div>
        </>
      ) : viewMode === 'all' ? (
        /* ---------------- All teams grid ---------------- */
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.teams.map((t) => (
            <TeamCard key={t.teamId} team={t} expandedKey={expandedKey} setExpandedKey={setExpandedKey} />
          ))}
        </div>
      ) : (
        /* ---------------- Single team ---------------- */
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {data.teams.map((t) => (
              <button
                key={t.teamId}
                onClick={() => setSelectedTeamId(t.teamId)}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                  t.teamId === selectedTeamId
                    ? 'bg-purple-700 text-white shadow-md scale-105'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-purple-50'
                }`}
                title={t.teamName}
              >
                {t.owner}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 px-6 py-4 text-white">
              <div className="flex items-end justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-xl font-bold">{team.owner}</h2>
                  <p className="text-purple-200 text-sm">{team.teamName}</p>
                </div>
                <div className="text-right text-sm text-purple-100">
                  <div>{team.players.length} players · {contractCount} under contract</div>
                  <div className="text-purple-300">If all kept: ${ifKeptAll} of ${data.cap}</div>
                  {team.penaltyTotal > 0 && (
                    <div className="text-rose-300 font-semibold">Cut penalties: −${team.penaltyTotal}</div>
                  )}
                  {team.adjustmentTotal != null && team.adjustmentTotal !== 0 && (
                    <div className="text-cyan-200 font-semibold">
                      Draft $ adj: {team.adjustmentTotal > 0 ? '+' : '−'}${Math.abs(team.adjustmentTotal)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {[...team.players].sort(byHierarchy).map((p, i) => {
                const m = meta(p.status);
                const key = `${team.teamId}-${i}`;
                const open = expandedKey === key;
                return (
                  <div key={key}>
                    <button
                      onClick={() => setExpandedKey(open ? null : key)}
                      className="w-full flex items-center gap-3 px-4 sm:px-6 py-3 hover:bg-gray-50 text-left"
                    >
                      <ChevronRight className={`h-4 w-4 flex-shrink-0 transition-transform ${open ? 'rotate-90 text-gray-500' : 'text-gray-300'}`} />
                      <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${m.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{p.player}</div>
                        <div className="text-xs text-gray-400">{acqShort(p.acquired)}</div>
                      </div>
                      <span className={`hidden sm:inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.badge}`}>
                        {m.label}
                      </span>
                      {p.cutCost != null ? (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-600 w-16 justify-end" title="Cost to cut">
                          <Scissors className="h-3 w-3" />${p.cutCost}
                        </span>
                      ) : <span className="w-16" />}
                      <div className="text-right w-24 flex-shrink-0">
                        <div className="font-bold text-gray-900 flex items-center justify-end">
                          <DollarSign className="h-4 w-4 text-emerald-600" />{p.keepCost}
                        </div>
                        <div className="text-[11px] text-gray-400">was ${p.prevCost}</div>
                      </div>
                    </button>
                    {open && <PlayerDetail p={p} />}
                  </div>
                );
              })}
            </div>
            {team.penalties.length > 0 && (
              <div className="bg-rose-50 border-t border-rose-100 px-4 sm:px-6 py-3">
                <div className="text-xs font-bold text-rose-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Scissors className="h-3.5 w-3.5" /> Cut penalties · docked from draft budget
                </div>
                {team.penalties.map((p, i) => (
                  <div key={`pen-${i}`} className="flex items-center justify-between text-sm py-0.5">
                    <span>
                      <span className="line-through text-rose-800 opacity-80">{p.player}</span>
                      <span className="ml-2 text-xs text-rose-500">{p.math || `cut in ${YR_LABEL[p.status] || p.status}`}</span>
                    </span>
                    <span className="font-bold text-rose-700">−${p.dock}</span>
                  </div>
                ))}
              </div>
            )}
            {team.adjustments.length > 0 && (
              <div className="bg-cyan-50 border-t border-cyan-100 px-4 sm:px-6 py-3">
                <div className="text-xs font-bold text-cyan-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <DollarSign className="h-3.5 w-3.5" /> Draft-dollar adjustments
                </div>
                {team.adjustments.map((a, i) => (
                  <div key={`adj-${i}`} className="flex items-center justify-between text-sm py-0.5">
                    <span className="text-cyan-900">{a.label}</span>
                    <span className={`font-bold ${a.amount < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {a.amount < 0 ? '−' : '+'}${Math.abs(a.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <p className="text-xs text-gray-400 mt-4 flex items-center gap-1">
        <ShieldCheck className="h-3.5 w-3.5" />
        Built {new Date(data.builtAt).toLocaleDateString()} · keep costs are options, not yet elected.
      </p>
    </div>
  );
};

export default Keepers;
